/**
 * Screen state: where Hub is, panels, the theme. Nothing here comes from
 * the server.
 */
import {
  BACKGROUND_SETTINGS,
  type BackgroundSetting,
  type Snowflake,
  type ThemeSetting,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { savedAccountLook } from '../lib/accountLook';
import { storage } from '../lib/storage';
import { useData } from './data';

export type HubView =
  | { kind: 'channel'; channelId: Snowflake }
  /** chat: open the channel's own chat (from the channel's menu, without joining). */
  | { kind: 'voice'; channelId: Snowflake; chat?: boolean }
  /** Home: a DM open, or the Friends page when channelId is absent. */
  | { kind: 'home'; channelId?: Snowflake; page?: 'requests' };

export type SettingsSection =
  | 'profile'
  | 'security'
  | 'privacy'
  /** Activity privacy: Share my activity, Registered games (2026-10-29). */
  | 'activity'
  | 'sessions'
  | 'appearance'
  | 'voice'
  | 'notifications'
  | 'sounds'
  | 'server'
  | 'about';

export interface AdminState {
  tab:
    | 'overview'
    | 'channels'
    | 'roles'
    | 'emoji'
    | 'invites'
    | 'automod'
    | 'audit'
    | 'bans'
    | 'deleted'
    | 'owner';
  /** The channel or role being edited, or "new". */
  id?: Snowflake | 'new';
  /** For a new channel: its type and parent. */
  newType?: 0 | 2 | 4;
  parentId?: Snowflake | null;
}

interface UiStore {
  hub: HubView | null;
  /**
   * The open server (Console v2: one account, many servers): the rail's
   * selection, the channel sidebar, Server settings. Null, or a server you
   * are no longer in: the official one (selectors `activeGuildId`).
   */
  guildId: Snowflake | null;
  /** The last text channel (Hub falls back to it). */
  lastTextId: Snowflake | null;
  /** Each server's last text channel, so the rail reopens it (Discord). */
  lastByGuild: Record<Snowflake, Snowflake>;
  showMembers: boolean;
  settings: SettingsSection | null;
  admin: AdminState | null;
  theme: ThemeSetting;
  /** The window behind the glass: the aurora (glow) or a solid colour. */
  background: BackgroundSetting;
  /** A message to scroll to once its channel is open (search results). */
  jump: { channelId: Snowflake; messageId: Snowflake } | null;
  set(p: Partial<Omit<UiStore, 'set'>>): void;
}

const isThemeSetting = (v: unknown): v is ThemeSetting =>
  v === 'pearl' || v === 'smoked' || v === 'system';
/**
 * The theme at start: this device's own choice; else, signed in, the
 * account's as last heard (READY confirms it); else System until the
 * console's default arrives (store/consoleDefaults.ts).
 */
function startTheme(): ThemeSetting {
  const own = storage.get('theme');
  if (isThemeSetting(own)) return own;
  const account = savedAccountLook().theme;
  return isThemeSetting(account) ? account : 'system';
}

export const isBackgroundSetting = (v: unknown): v is BackgroundSetting =>
  (BACKGROUND_SETTINGS as readonly unknown[]).includes(v);
/** The background at start, the way the theme's is chosen (else the aurora). */
function startBackground(): BackgroundSetting {
  const own = storage.get('background');
  if (isBackgroundSetting(own)) return own;
  const account = savedAccountLook().background;
  return isBackgroundSetting(account) ? account : 'glow';
}

/**
 * Keys an app from before Hub only (2026-10-28) left on this device: the
 * last mode, the Comms tree's collapsed channels and the whisper lists.
 * Nothing reads them; they are dropped once so they never come back.
 */
export const RETIRED_STORAGE_KEYS = ['mode', 'comms-collapsed', 'whisper-lists'] as const;
for (const k of RETIRED_STORAGE_KEYS) storage.remove(k);

export const useUi = create<UiStore>((set) => ({
  hub: null,
  guildId: null,
  lastTextId: storage.get('last-text'),
  lastByGuild: storage.getJson<Record<Snowflake, Snowflake>>('last-text-by-guild', {}),
  showMembers: storage.get('members') !== '0',
  settings: null,
  admin: null,
  theme: startTheme(),
  background: startBackground(),
  jump: null,
  set: (p) => set(p),
}));

/** Pearl/Smoked follow the system unless chosen (`data-theme` on <html>). */
export function applyTheme(theme: ThemeSetting): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'pearl') root.dataset.theme = 'light';
  else if (theme === 'smoked') root.dataset.theme = 'dark';
  else delete root.dataset.theme;
  paintWindowColour();
}

/**
 * Each solid background in the light and the dark theme (tokens.css
 * --solid-*, the same colours): a dark choice shown in the light theme is
 * its light pair, and the other way round.
 */
export const SOLID_COLOURS: Record<
  Exclude<BackgroundSetting, 'glow'>,
  { light: string; dark: string }
> = {
  black: { light: '#ffffff', dark: '#000000' },
  white: { light: '#ffffff', dark: '#000000' },
  charcoal: { light: '#f2f2f7', dark: '#1c1c1e' },
  grey: { light: '#f2f2f7', dark: '#1c1c1e' },
  midnight: { light: '#f2f2f7', dark: '#0b1226' },
};

/** The swatch a background shows as in a theme (black is white in the light theme). */
export function shownSolid(
  bg: BackgroundSetting,
  dark: boolean,
): Exclude<BackgroundSetting, 'glow'> | null {
  if (bg === 'glow') return null;
  if (dark) return bg === 'white' ? 'black' : bg === 'grey' ? 'charcoal' : bg;
  return bg === 'black' || bg === 'white' ? 'white' : 'grey';
}

/** True when the dark theme is showing (chosen, or the system's under System). */
export function themeIsDark(): boolean {
  const theme = useUi.getState().theme;
  return (
    theme === 'smoked' ||
    (theme === 'system' && !!globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches)
  );
}

/** The window's own colour in the browser's chrome (meta theme-color), as index.html paints it first (I23). */
function paintWindowColour(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const dark =
    root.dataset.theme === 'dark' ||
    (root.dataset.theme !== 'light' &&
      !!globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches);
  const bg = root.dataset.bg;
  const solid = isBackgroundSetting(bg) && bg !== 'glow' ? SOLID_COLOURS[bg] : null;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute(
      'content',
      solid ? (dark ? solid.dark : solid.light) : dark ? '#0c0e13' : '#e6e9ef',
    );
}

export function setTheme(theme: ThemeSetting): void {
  storage.set('theme', theme);
  applyTheme(theme);
  useUi.getState().set({ theme });
}

/**
 * Shows a background: `data-bg` on <html> for a solid one (tokens.css turns
 * the aurora and the grain off and paints --bg-app that colour); none for
 * the aurora. The glass panels, their corners and prefers-reduced-transparency
 * are untouched.
 */
export function applyBackground(bg: BackgroundSetting): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (bg === 'glow') delete root.dataset.bg;
  else root.dataset.bg = bg;
  paintWindowColour();
}

/** A background chosen on this device (store/look.ts saves it to the account). */
export function setBackground(bg: BackgroundSetting): void {
  storage.set('background', bg);
  applyBackground(bg);
  useUi.getState().set({ background: bg });
}

// The account's theme (READY, or a change on another device) is applied by
// store/look.ts `applyAccountLook` (settings fixes, with SN-B11).

/* ---------- reduced motion and reduced transparency (I12; the toggles are WP5's) ---------- */

/** The person's choice for motion; "system" follows prefers-reduced-motion. */
export type MotionPref = 'system' | 'reduce' | 'full';
/** The person's choice for transparency; "full" still follows prefers-reduced-transparency. */
export type GlassPref = 'full' | 'flat';

const isMotionPref = (v: unknown): v is MotionPref =>
  v === 'system' || v === 'reduce' || v === 'full';
const isGlassPref = (v: unknown): v is GlassPref => v === 'full' || v === 'flat';
const media = (q: string) => globalThis.matchMedia?.(q) ?? null;

export function motionPref(): MotionPref {
  const v = storage.get('motion');
  return isMotionPref(v) ? v : 'system';
}
export function glassPref(): GlassPref {
  const v = storage.get('glass');
  return isGlassPref(v) ? v : 'full';
}

/**
 * Sets `data-motion` and `data-glass` on <html> from the stored choice or
 * the system (tokens.css turns every travel, press and long duration off
 * under `data-motion="reduce"`, and every backdrop blur off under
 * `data-glass="flat"`).
 */
export function applyMotionAndGlass(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const m = motionPref();
  const reduce =
    m === 'reduce' || (m === 'system' && !!media('(prefers-reduced-motion: reduce)')?.matches);
  root.dataset.motion = reduce ? 'reduce' : 'full';
  const flat = glassPref() === 'flat' || !!media('(prefers-reduced-transparency: reduce)')?.matches;
  root.dataset.glass = flat ? 'flat' : 'full';
}

export function setMotionPref(pref: MotionPref): void {
  storage.set('motion', pref);
  applyMotionAndGlass();
}

export function setGlassPref(pref: GlassPref): void {
  storage.set('glass', pref);
  applyMotionAndGlass();
}

// follow the system while the choice is "system" (and the transparency setting always)
for (const q of ['(prefers-reduced-motion: reduce)', '(prefers-reduced-transparency: reduce)'])
  media(q)?.addEventListener?.('change', applyMotionAndGlass);
applyMotionAndGlass();

export function goHub(view: HubView): void {
  const patch: Partial<UiStore> = { hub: view };
  // a server's channel opens that server (Console v2): the rail and sidebar follow
  const guildId =
    view.kind === 'home' ? null : (useData.getState().channels[view.channelId]?.guild_id ?? null);
  if (guildId) patch.guildId = guildId;
  if (view.kind === 'channel') {
    patch.lastTextId = view.channelId;
    storage.set('last-text', view.channelId);
    if (guildId) {
      const lastByGuild = { ...useUi.getState().lastByGuild, [guildId]: view.channelId };
      patch.lastByGuild = lastByGuild;
      storage.setJson('last-text-by-guild', lastByGuild);
    }
  }
  useUi.getState().set(patch);
}

/**
 * Opens a server from the rail: its last text channel, else (an empty
 * channel id) the first one Hub finds there (`HubMain`'s fallback).
 */
export function goGuild(guildId: Snowflake): void {
  const d = useData.getState();
  const last = useUi.getState().lastByGuild[guildId];
  useUi.getState().set({ guildId });
  goHub({
    kind: 'channel',
    channelId: last && d.channels[last]?.guild_id === guildId ? last : '',
  });
}

/**
 * A Settings page with unsaved changes (Profile) asks before it is left:
 * resolves true to leave (the changes are dropped), false to stay. Live
 * bug 2026-10-28: switching tab or pressing Escape dropped a typed bio
 * without a word.
 */
let unsavedGuard: (() => Promise<boolean>) | null = null;

export function setUnsavedGuard(guard: () => Promise<boolean>): () => void {
  unsavedGuard = guard;
  return () => {
    if (unsavedGuard === guard) unsavedGuard = null;
  };
}

/** True when the open Settings page may be left (nothing unsaved, or the person chose to drop it). */
export async function mayLeaveSettingsPage(): Promise<boolean> {
  return unsavedGuard ? unsavedGuard() : true;
}

/** Opens Settings on a page, or closes it (null), asking first when the open page has unsaved changes. */
export function goSettings(section: SettingsSection | null): void {
  const open = useUi.getState().settings;
  if (open === section) return;
  if (open === null || !unsavedGuard) {
    useUi.getState().set({ settings: section });
    return;
  }
  void mayLeaveSettingsPage().then((ok) => {
    if (ok) useUi.getState().set({ settings: section });
  });
}

export function openSettings(section: SettingsSection = 'appearance'): void {
  goSettings(section);
}

export function openAdmin(a: AdminState): void {
  useUi.getState().set({ admin: a });
}
